// ============================================================================
// AdminEnvelopesPanel — signature envelopes (Build S3, Task #19).
// ============================================================================
// Sidebar: #admin/envelopes (Signatures & Agreements group). LIST + DETAIL
// (route-driven, S2 Documents precedent). Detail: parties (add/remove before
// send), send/void, audit-trail viewer, certificate download.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import {
  Loader2, AlertCircle, PenLine, Send, Ban, Download, ArrowLeft, Plus, Trash2, Users,
} from 'lucide-react';
import {
  api, type AdminEnvelope, type AdminEnvelopeDetail, type AdminDocument,
  type AdminFarm, type AdminAgreement, type EnvelopePartyInput, type SignInput,
} from '../api';
import { SignatureCapture } from './SignatureCapture';

interface Props {
  token: string;
  envelopeId?: string;
}

const STATUS_TONES: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  sent: 'bg-blue-100 text-blue-800',
  partially_signed: 'bg-amber-100 text-amber-800',
  completed: 'bg-green-100 text-green-800',
  declined: 'bg-red-100 text-red-700',
  voided: 'bg-gray-200 text-gray-600',
  expired: 'bg-gray-200 text-gray-600',
};

function StatusPill({ status }: { status: string }) {
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_TONES[status] ?? 'bg-gray-100 text-gray-700'}`}>
      {status.replace('_', ' ')}
    </span>
  );
}

export function AdminEnvelopesPanel({ token, envelopeId }: Props) {
  if (envelopeId !== undefined) return <EnvelopeDetail token={token} envelopeId={envelopeId} />;
  return <EnvelopeList token={token} />;
}

// ============================================================================
// LIST
// ============================================================================
function EnvelopeList({ token }: { token: string }) {
  const [envelopes, setEnvelopes] = useState<AdminEnvelope[]>([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true); setError('');
    try {
      setEnvelopes((await api.adminEnvelopes(token, { status: statusFilter || undefined })).envelopes);
    } catch (e: any) { setError(e?.message || 'Could not load envelopes'); }
    finally { setLoading(false); }
  }, [token, statusFilter]);

  useEffect(() => { void refresh(); }, [refresh]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-uif-green-dark flex items-center gap-2">
            <PenLine className="h-5 w-5" /> Signature Envelopes
          </h1>
          <p className="text-sm text-gray-500">Internal E-SIGN ceremonies over documents.</p>
        </div>
        <button type="button" onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-3 py-2 text-sm hover:bg-uif-green-dark">
          <Plus className="h-4 w-4" /> New envelope
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-3">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm">
          <option value="">All statuses</option>
          {Object.keys(STATUS_TONES).map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">{error}</div>}
      {loading ? (
        <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-uif-green" /></div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-3 py-2">Title</th>
                <th className="px-3 py-2">Subject</th>
                <th className="px-3 py-2">Farm</th>
                <th className="px-3 py-2">Signed</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {envelopes.map((e) => (
                <tr key={e.envelope_id} className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => { window.location.hash = `#admin/envelopes/${e.envelope_id}`; }}>
                  <td className="px-3 py-2 font-medium text-gray-900">{e.title}</td>
                  <td className="px-3 py-2 text-gray-600">{e.subject_title ?? '—'}</td>
                  <td className="px-3 py-2 text-gray-600">{e.farm_code ?? '—'}</td>
                  <td className="px-3 py-2 text-gray-600">{e.signed_count}/{e.party_count}</td>
                  <td className="px-3 py-2"><StatusPill status={e.status} /></td>
                </tr>
              ))}
              {envelopes.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-8 text-center text-gray-400">No envelopes yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {createOpen && (
        <CreateEnvelopeModal token={token} onClose={() => setCreateOpen(false)}
          onCreated={(id) => { window.location.hash = `#admin/envelopes/${id}`; }} />
      )}
    </div>
  );
}

function CreateEnvelopeModal({ token, onClose, onCreated }: {
  token: string; onClose: () => void; onCreated: (id: string) => void;
}) {
  const [documents, setDocuments] = useState<AdminDocument[]>([]);
  const [draftAgreements, setDraftAgreements] = useState<AdminAgreement[]>([]);
  const [subjectId, setSubjectId] = useState('');
  const [agreementId, setAgreementId] = useState('');
  const [title, setTitle] = useState('');
  const [signingOrder, setSigningOrder] = useState<'sequential' | 'parallel'>('sequential');
  const [instructions, setInstructions] = useState('');
  const [expiresDays, setExpiresDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.adminDocuments(token, {}).then((r) => {
      // Signable subjects must be PDF (S3 boundary — the API enforces too).
      setDocuments(r.documents.filter((d) => d.mime_type === 'application/pdf' && d.current_version_id));
    }).catch(() => undefined);
    // F-1 remediation: envelopes LINK to an existing draft agreement here —
    // creating an agreement first no longer dead-ends the envelope flow.
    api.adminAgreements(token, { status: 'draft' })
      .then((r) => setDraftAgreements(r.agreements))
      .catch(() => undefined);
  }, [token]);

  // Auto-suggest the matching draft agreement when the chosen document is
  // farm-scoped and exactly one draft agreement exists for that farm.
  useEffect(() => {
    if (!subjectId) return;
    const doc = documents.find((d) => d.document_id === subjectId);
    if (!doc?.farm_id) return;
    const matches = draftAgreements.filter((a) => a.farm_id === doc.farm_id);
    if (matches.length === 1) setAgreementId(matches[0].agreement_id);
  }, [subjectId, documents, draftAgreements]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!subjectId || !title.trim()) return;
    setBusy(true); setError('');
    try {
      const r = await api.adminEnvelopeCreate(token, {
        subject_type: 'document', subject_id: subjectId, title: title.trim(),
        signing_order: signingOrder, signing_instructions: instructions.trim() || null,
        expires_in_days: expiresDays,
        ...(agreementId ? { agreement_id: agreementId } : {}),
      });
      onCreated(r.envelope_id);
    } catch (err: any) { setError(err?.message || 'Could not create envelope'); setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl w-full max-w-lg p-5 space-y-3">
        <h2 className="text-lg font-bold text-uif-green-dark">New signature envelope</h2>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">{error}</div>}
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Document (PDF) *</span>
          <select required value={subjectId} onChange={(e) => setSubjectId(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm">
            <option value="">Select…</option>
            {documents.map((d) => (
              <option key={d.document_id} value={d.document_id}>
                {d.title}{d.farm_code ? ` (${d.farm_code})` : ''} — {d.status}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Link to draft agreement (optional — executes it on completion)</span>
          <select value={agreementId} onChange={(e) => setAgreementId(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm">
            <option value="">None</option>
            {draftAgreements.map((a) => (
              <option key={a.agreement_id} value={a.agreement_id}>
                {a.farm_code} — {a.template_version} (draft)
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Envelope title *</span>
          <input required value={title} onChange={(e) => setTitle(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Signing order</span>
            <select value={signingOrder} onChange={(e) => setSigningOrder(e.target.value as any)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm">
              <option value="sequential">Sequential</option>
              <option value="parallel">Parallel</option>
            </select>
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Expires (days)</span>
            <input type="number" min={1} max={365} value={expiresDays}
              onChange={(e) => setExpiresDays(Number(e.target.value) || 30)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
          </label>
        </div>
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Signing instructions (shown to signers)</span>
          <textarea rows={3} value={instructions} onChange={(e) => setInstructions(e.target.value)}
            placeholder={'Please review the AMP Producer Participation Agreement for your farm. You will be asked to (1) review the document, (2) consent to electronic records, and (3) sign by typing your name or drawing your signature. Signing order: the producer signs first; Up in Farms countersigns to execute. Questions: support@upinfarms.com or your farm coordinator.'}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
        </label>
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="rounded-md border border-gray-300 px-4 py-2 text-sm">Cancel</button>
          <button type="submit" disabled={busy || !subjectId || !title.trim()}
            className="rounded-md bg-uif-green text-white font-semibold px-4 py-2 text-sm disabled:opacity-40">
            {busy ? 'Creating…' : 'Create draft'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ============================================================================
// DETAIL
// ============================================================================
function EnvelopeDetail({ token, envelopeId }: { token: string; envelopeId: string }) {
  const [detail, setDetail] = useState<AdminEnvelopeDetail | null>(null);
  const [farms, setFarms] = useState<AdminFarm[]>([]);
  const [members, setMembers] = useState<Array<{ member_id: number; member_name: string; farm_code: string; signature_authority: boolean; email: string | null }>>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [ceremonyPhase, setCeremonyPhase] = useState<'consent' | 'sign' | null>(null);

  const refresh = useCallback(async () => {
    setError('');
    try { setDetail(await api.adminEnvelopeDetail(token, envelopeId)); }
    catch (e: any) { setError(e?.message || 'Could not load envelope'); }
  }, [token, envelopeId]);

  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => {
    api.adminFarms(token, { include_test: true }).then((r) => setFarms(r.farms)).catch(() => undefined);
  }, [token]);

  async function act(fn: () => Promise<unknown>, confirmText?: string) {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(true); setError('');
    try { await fn(); await refresh(); }
    catch (e: any) { setError(e?.message || 'Action failed'); }
    finally { setBusy(false); }
  }

  async function downloadCertificate() {
    try {
      const r = await api.adminEnvelopeCertificateUrl(token, envelopeId);
      window.location.href = r.download_url;
    } catch (e: any) { setError(e?.message || 'Could not download certificate'); }
  }

  // F-4: the UIF signing ceremony — same consent-then-capture flow as the
  // producer surface, under the authenticated admin session.
  async function ceremonyConsent() {
    setBusy(true); setError('');
    try {
      await api.adminEnvelopeConsent(token, envelopeId);
      await refresh();
      setCeremonyPhase('sign');
    } catch (e: any) { setError(e?.message || 'Could not record consent'); }
    finally { setBusy(false); }
  }
  async function ceremonySign(input: SignInput) {
    setBusy(true); setError('');
    try {
      await api.adminEnvelopeSign(token, envelopeId, input);
      setCeremonyPhase(null);
      await refresh();
    } catch (e: any) { setError(e?.message || 'Could not record signature'); }
    finally { setBusy(false); }
  }

  if (!detail) {
    return error
      ? <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">{error}</div>
      : <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-uif-green" /></div>;
  }
  const env = detail.envelope;
  const isDraft = env.status === 'draft';

  return (
    <div className="space-y-4">
      <a href="#admin/envelopes" className="inline-flex items-center gap-1 text-sm text-uif-green-dark hover:underline">
        <ArrowLeft className="h-4 w-4" /> All envelopes
      </a>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-uif-green-dark">{env.title}</h1>
          <div className="text-sm text-gray-500 flex items-center gap-2">
            <StatusPill status={env.status} />
            {detail.subject && <span>Subject: {detail.subject.title}</span>}
            {detail.agreement && <span>· Agreement: {detail.agreement.template_version} ({detail.agreement.status})</span>}
          </div>
        </div>
        <div className="flex gap-2">
          {isDraft && (
            <button type="button" disabled={busy || detail.parties.length === 0}
              onClick={() => void act(() => api.adminEnvelopeSend(token, envelopeId),
                `Send to ${detail.parties.length} part${detail.parties.length === 1 ? 'y' : 'ies'}?`)}
              className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-3 py-2 text-sm disabled:opacity-40 hover:bg-uif-green-dark">
              <Send className="h-4 w-4" /> Send
            </button>
          )}
          {['draft', 'sent', 'partially_signed'].includes(env.status) && (
            <button type="button" disabled={busy}
              onClick={() => void act(() => api.adminEnvelopeVoid(token, envelopeId), 'Void this envelope? Signers will be notified.')}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 text-gray-700 px-3 py-2 text-sm hover:border-red-400 hover:text-red-600">
              <Ban className="h-4 w-4" /> Void
            </button>
          )}
          {env.status === 'completed' && (
            <button type="button" onClick={() => void downloadCertificate()}
              className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-3 py-2 text-sm hover:bg-uif-green-dark">
              <Download className="h-4 w-4" /> Certificate
            </button>
          )}
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm flex items-start gap-2"><AlertCircle className="h-4 w-4 mt-0.5" />{error}</div>}

      {/* F-4: the UIF signing ceremony — appears when the signed-in admin is
          a named UIF party, the envelope is live, and it's their turn. */}
      {detail.my_party && ['sent', 'partially_signed'].includes(env.status)
        && detail.my_party.status !== 'signed' && detail.my_party.status !== 'declined' && (
        <div className="bg-uif-green/5 border-2 border-uif-green rounded-lg p-4 space-y-3">
          <h2 className="font-semibold text-uif-green-dark">
            Your signature is requested (signing as {detail.my_party.display_name})
          </h2>
          {!detail.my_party.my_turn ? (
            <p className="text-sm text-amber-700">Waiting on an earlier signer — the ceremony unlocks when it is your turn.</p>
          ) : ceremonyPhase === null ? (
            <button type="button" onClick={() => setCeremonyPhase(detail.my_party!.consent_recorded ? 'sign' : 'consent')}
              className="rounded-md bg-uif-green text-white font-semibold px-4 py-2.5 hover:bg-uif-green-dark">
              {detail.my_party.consent_recorded ? 'Continue to signature' : 'Review and sign'}
            </button>
          ) : ceremonyPhase === 'consent' && detail.consent_language ? (
            <div className="space-y-3">
              <pre className="whitespace-pre-wrap text-sm text-gray-800 font-sans leading-relaxed bg-white border border-gray-200 rounded-md p-3">
                {detail.consent_language}
              </pre>
              <button type="button" disabled={busy} onClick={() => void ceremonyConsent()}
                className="w-full rounded-md bg-uif-green text-white font-semibold px-4 py-2.5 disabled:opacity-40 hover:bg-uif-green-dark">
                {busy ? 'Recording…' : 'I agree and consent'}
              </button>
            </div>
          ) : (
            <SignatureCapture
              displayName={detail.my_party.display_name}
              busy={busy}
              onSubmit={(input) => void ceremonySign(input)}
            />
          )}
        </div>
      )}
      {detail.my_party?.status === 'signed' && env.status !== 'completed' && (
        <div className="bg-green-50 border border-green-200 rounded-md px-3 py-2 text-sm text-green-800">
          Your signature is recorded — waiting on the remaining signer(s).
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2"><Users className="h-4 w-4" /> Parties</h2>
          {isDraft && (
            <button type="button" onClick={() => setAddOpen(true)}
              className="inline-flex items-center gap-1 text-sm text-uif-green-dark hover:underline">
              <Plus className="h-4 w-4" /> Add party
            </button>
          )}
        </div>
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-gray-500 uppercase">
            <tr>
              <th className="py-1 pr-3">#</th><th className="py-1 pr-3">Name</th>
              <th className="py-1 pr-3">Kind</th><th className="py-1 pr-3">Email</th>
              <th className="py-1 pr-3">Status</th><th className="py-1 pr-3">Signed</th><th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {detail.parties.map((p) => (
              <tr key={p.party_id}>
                <td className="py-2 pr-3">{p.order_index}</td>
                <td className="py-2 pr-3 font-medium">{p.display_name}{p.title_role ? <span className="text-gray-500 font-normal"> — {p.title_role}</span> : null}</td>
                <td className="py-2 pr-3">{p.signer_kind}</td>
                <td className="py-2 pr-3 text-gray-600">{p.email}</td>
                <td className="py-2 pr-3"><StatusPill status={p.status} /></td>
                <td className="py-2 pr-3 text-gray-600">{p.signed_at ? new Date(p.signed_at).toLocaleString() : '—'}</td>
                <td className="py-2 text-right">
                  {isDraft && (
                    <button type="button" disabled={busy}
                      onClick={() => void act(() => api.adminEnvelopePartyDelete(token, envelopeId, p.party_id))}
                      className="text-gray-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                  )}
                </td>
              </tr>
            ))}
            {detail.parties.length === 0 && (
              <tr><td colSpan={7} className="py-4 text-center text-gray-400">No parties yet — add signers before sending.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <button type="button" onClick={() => setAuditOpen(!auditOpen)}
          className="font-semibold text-gray-900 text-sm hover:text-uif-green-dark">
          Audit trail ({(env.audit_trail as unknown[]).length} events) {auditOpen ? '▾' : '▸'}
        </button>
        {auditOpen && (
          <pre className="mt-2 text-xs bg-gray-50 border border-gray-100 rounded p-3 overflow-x-auto max-h-80 overflow-y-auto">
            {JSON.stringify(env.audit_trail, null, 2)}
          </pre>
        )}
      </div>

      {addOpen && (
        <AddPartyModal
          token={token} farms={farms} members={members} setMembers={setMembers}
          nextOrder={detail.parties.length + 1}
          onSubmit={async (input) => {
            await api.adminEnvelopePartyAdd(token, envelopeId, input);
            setAddOpen(false);
            await refresh();
          }}
          onClose={() => setAddOpen(false)}
        />
      )}
    </div>
  );
}

function AddPartyModal({ token, farms, members, setMembers, nextOrder, onSubmit, onClose }: {
  token: string;
  farms: AdminFarm[];
  members: Array<{ member_id: number; member_name: string; farm_code: string; signature_authority: boolean; email: string | null }>;
  setMembers: (m: any[]) => void;
  nextOrder: number;
  onSubmit: (input: EnvelopePartyInput) => Promise<void>;
  onClose: () => void;
}) {
  const [kind, setKind] = useState<'UIF' | 'PRODUCER' | 'EXTERNAL'>('PRODUCER');
  const [orderIndex, setOrderIndex] = useState(nextOrder);
  const [farmId, setFarmId] = useState<number | ''>('');
  const [memberId, setMemberId] = useState<number | ''>('');
  const [externalEmail, setExternalEmail] = useState('');
  const [externalName, setExternalName] = useState('');
  const [titleRole, setTitleRole] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (kind === 'PRODUCER' && farmId !== '') {
      api.adminFarm(token, Number(farmId)).then((r: any) => {
        setMembers((r.members ?? []).map((m: any) => ({
          member_id: m.member_id, member_name: m.member_name,
          farm_code: r.farm?.farm_code ?? '', signature_authority: m.signature_authority,
          email: m.email,
        })));
      }).catch(() => setMembers([]));
    }
  }, [kind, farmId, token, setMembers]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError('');
    const input: EnvelopePartyInput = { signer_kind: kind, order_index: orderIndex, title_role: titleRole.trim() || null };
    if (kind === 'PRODUCER') input.farm_member_id = Number(memberId);
    if (kind === 'EXTERNAL') { input.external_email = externalEmail.trim(); input.external_name = externalName.trim(); }
    // UIF: admin_user_id omitted -> the API defaults to the signed-in admin.
    try {
      await onSubmit(input);
    } catch (err: any) { setError(err?.message || 'Could not add party'); setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl w-full max-w-lg p-5 space-y-3">
        <h2 className="text-lg font-bold text-uif-green-dark">Add party</h2>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">{error}</div>}
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Signer kind</span>
            <select value={kind} onChange={(e) => setKind(e.target.value as any)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm">
              <option value="PRODUCER">Producer</option>
              <option value="UIF">UIF (me)</option>
              <option value="EXTERNAL">External</option>
            </select>
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Signing position</span>
            <input type="number" min={1} max={20} value={orderIndex}
              onChange={(e) => setOrderIndex(Number(e.target.value) || 1)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
          </label>
        </div>

        {kind === 'PRODUCER' && (
          <>
            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">Farm *</span>
              <select required value={farmId} onChange={(e) => { setFarmId(e.target.value === '' ? '' : Number(e.target.value)); setMemberId(''); }}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm">
                <option value="">Select…</option>
                {farms.map((f) => <option key={f.farm_id} value={f.farm_id}>{f.farm_code} — {f.farm_name}{f.is_test ? ' [TEST]' : ''}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">Member (must hold signature authority) *</span>
              <select required value={memberId} onChange={(e) => setMemberId(e.target.value === '' ? '' : Number(e.target.value))}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm">
                <option value="">Select…</option>
                {members.map((m) => (
                  <option key={m.member_id} value={m.member_id} disabled={!m.signature_authority || !m.email}>
                    {m.member_name}{!m.signature_authority ? ' (no signature authority)' : !m.email ? ' (no email)' : ''}
                  </option>
                ))}
              </select>
            </label>
          </>
        )}

        {kind === 'EXTERNAL' && (
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">Name *</span>
              <input required value={externalName} onChange={(e) => setExternalName(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">Email *</span>
              <input required type="email" value={externalEmail} onChange={(e) => setExternalEmail(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
            </label>
          </div>
        )}

        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Title / role (shown on the signature page)</span>
          <input value={titleRole} onChange={(e) => setTitleRole(e.target.value)} placeholder="e.g. Owner-Operator"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
        </label>

        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="rounded-md border border-gray-300 px-4 py-2 text-sm">Cancel</button>
          <button type="submit" disabled={busy || (kind === 'PRODUCER' && memberId === '')}
            className="rounded-md bg-uif-green text-white font-semibold px-4 py-2 text-sm disabled:opacity-40">
            {busy ? 'Adding…' : 'Add party'}
          </button>
        </div>
      </form>
    </div>
  );
}
